import { Check, Eye } from "lucide-react";
import { Reveal, Label } from "./motion";
import { IMAGES } from "@/data/content";

const POINTS = [
  {
    title: "Eases everyday joint comfort",
    body: "Gentle cannabinoid support paired with joint-friendly ingredients to help stiff mornings feel smoother.",
  },
  {
    title: "Encourages natural mobility",
    body: "Supporting the comfort behind the movement, so favourite walks and welcome-home zoomies stay in reach.",
  },
  {
    title: "Softens restlessness & unease",
    body: "A calmer baseline for storms, separation, and the quiet hours — for more settled, restful nights.",
  },
  {
    title: "Supports golden-years vitality",
    body: "Part of a thoughtful senior routine alongside good food, gentle exercise, and regular vet visits.",
  },
];

export const Solution = () => (
  <section id="benefits" className="bg-[#FAF7F2] px-5 py-20 sm:px-8 lg:py-28">
    <div className="mx-auto grid max-w-7xl items-center gap-12 lg:grid-cols-2 lg:gap-16">
      <Reveal className="order-2 lg:order-1">
        <div className="relative">
          <div className="absolute -inset-3 rounded-[2.25rem] bg-[#E2ECE7]" aria-hidden />
          <div className="relative overflow-hidden rounded-[2rem] border border-[#E6E0D4] shadow-lg">
            <img
              src={IMAGES.solution}
              alt="Comfortable senior poodle resting on a soft couch"
              className="h-[420px] w-full object-cover sm:h-[520px]"
            />
          </div>
          <div className="absolute -bottom-5 left-6 rounded-2xl border border-[#E6E0D4] bg-[#FFFDF9] px-5 py-4 shadow-xl">
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#8A8478]">Formulated with</div>
            <div className="font-serif text-lg font-semibold text-[#2D4338]">Aging dogs in mind</div>
          </div>
        </div>
      </Reveal>

      <div className="order-1 lg:order-2">
        <Reveal><Label>The CBDoodle Approach</Label></Reveal>
        <Reveal delay={0.05}>
          <h2 className="mt-4 font-serif text-3xl font-semibold leading-tight text-[#2C2B29] sm:text-4xl lg:text-[2.9rem]">
            Comfort, calm, and quality of life — the gentle way.
          </h2>
        </Reveal>
        <Reveal delay={0.1}>
          <p className="mt-5 font-sans leading-relaxed text-[#5C5850]">
            CBDoodle is a concept for high-quality, hemp-derived CBD imagined specifically for senior dogs.
            The idea: pair broad-spectrum CBD with supportive botanicals to help your poodle feel more like
            themselves — soft claims, real care, no cure promises.
          </p>
        </Reveal>

        <div className="mt-8 grid gap-4 sm:grid-cols-2">
          {POINTS.map((p, i) => (
            <Reveal key={p.title} delay={0.12 + i * 0.07}>
              <div className="flex gap-3.5 rounded-2xl border border-[#E6E0D4] bg-[#FFFDF9] p-5">
                <span className="mt-0.5 inline-flex h-6 w-6 flex-none items-center justify-center rounded-full bg-[#4A6B5D] text-white">
                  <Check size={14} strokeWidth={3} />
                </span>
                <div>
                  <h3 className="font-sans text-[15px] font-semibold text-[#2C2B29]">{p.title}</h3>
                  <p className="mt-1 font-sans text-sm leading-relaxed text-[#5C5850]">{p.body}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.4}>
          <div data-testid="cbdoodle-eye-note" className="mt-5 flex gap-3.5 rounded-2xl border border-[#C58F3B]/30 bg-[#FDF6E7] p-5">
            <span className="mt-0.5 inline-flex h-8 w-8 flex-none items-center justify-center rounded-full bg-[#C58F3B] text-white">
              <Eye size={16} />
            </span>
            <div>
              <h3 className="font-sans text-[15px] font-semibold text-[#7a5c22]">A gentle note on senior eyes</h3>
              <p className="mt-1 font-sans text-sm leading-relaxed text-[#8a6a32]">
                Poodles and many doodles are genetically prone to cataracts and other vision changes with age.
                CBDoodle won't treat or reverse eye conditions — only your veterinarian can help there — but
                staying calm and comfortable can ease the anxiety and disorientation that often come with fading
                eyesight. Keep up regular vet eye checks.
              </p>
            </div>
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);
